/* oxlint-disable @typescript-eslint/no-explicit-any */

import { bunja } from 'bunja';
import { atom } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';

import { postMfaPasskeyAuthApi } from '@flipster/apis/v1/mfa';
import { MfaScope } from '@flipster/apis/v1/mfa/types';
import {
  deleteUserPasskeyCredentialApi,
  getUserPasskeyCredentialsApi,
  postUserPasskeyAuthenticationApi,
} from '@flipster/apis/v1/user';
import { PasskeyItem } from '@flipster/features/src/passkey/PasskeyListModal';
import type { PasskeyVerifyStep } from '@flipster/features/src/passkey/VerifyWithPasskeyModal';
import { authBunja } from '@flipster/features/src/shared/auth/state';
import { overlayBunja } from '@flipster/overlay';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { convertDate } from '@flipster/utils/datetime';
import { getErrorMessage } from '@flipster/utils/error';

import PasskeyLoginFlow from './PasskeyLoginFlow';
import { getCredential, registerPasskey, toBase64 } from './core';

export const passkeyApiBunja = bunja(() => {
  const authBunjaInstance = bunja.use(authBunja);
  const { isCookieLoggedInAtom } = authBunjaInstance;
  const getUserPasskeyCredentialsAtom = atomWithQuery((get) => {
    const isCookieLoggedIn = get(isCookieLoggedInAtom);
    return {
      queryKey: ['getUserPasskeyCredentials', isCookieLoggedIn],
      queryFn: () => getUserPasskeyCredentialsApi(),
      enabled: !!isCookieLoggedIn,
    };
  });
  const credentialExistsAtom = atom((get) => {
    const { data: userPasskeyCredentials } = get(getUserPasskeyCredentialsAtom);
    return Boolean(userPasskeyCredentials?.data.credentials.length);
  });
  return { getUserPasskeyCredentialsAtom, credentialExistsAtom };
});

export const passkeyLoginFlowBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const authBunjaInstance = bunja.use(authBunja);
  const overlayInstance = bunja.use(overlayBunja);
  const { getMeAtom } = authBunjaInstance;
  const { open, close } = overlayInstance;
  const loginStepAtom = atom<PasskeyVerifyStep>('IDLE');

  async function loginWithPasskey() {
    try {
      store.set(loginStepAtom, 'VERIFYING');
      const credential = await getCredential();
      await postUserPasskeyAuthenticationApi({
        body: {
          id: credential.id,
          rawId: toBase64(credential.rawId),
          type: credential.type,
          authenticatorAttachment: credential.authenticatorAttachment || '',
          response: {
            clientDataJSON: toBase64(credential.response.clientDataJSON),
            authenticatorData: toBase64((credential.response as any).authenticatorData),
            signature: toBase64((credential.response as any).signature),
          },
        },
        authMethod: 'COOKIE',
        decodeChallenge: true,
      });

      store.set(loginStepAtom, 'VERIFIED');
      await store.get(getMeAtom).refetch();
      close();
    } catch (error) {
      snackbar.error({ message: getErrorMessage(error) });
      store.set(loginStepAtom, 'FAIL');
    }
  }

  function openPasskeyLoginModal() {
    open(() => <PasskeyLoginFlow />);
    loginWithPasskey();
  }

  return {
    loginStepAtom,
    loginWithPasskey,
    openPasskeyLoginModal,
  };
});

interface PasskeyAuthParams {
  mfaScope: MfaScope;
  mfaSessionToken: string;
  authToken?: string;
  onComplete?: () => void;
  onFail?: () => void;
}

export const passkeyMfaFlowBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const verifyStepAtom = atom<PasskeyVerifyStep>('IDLE');

  async function mfaWithPasskey({ mfaScope, mfaSessionToken, authToken, onComplete, onFail }: PasskeyAuthParams) {
    try {
      store.set(verifyStepAtom, 'VERIFYING');
      const credential = await getCredential();
      await postMfaPasskeyAuthApi({
        body: {
          id: credential.id,
          rawId: toBase64(credential.rawId),
          type: credential.type,
          authenticatorAttachment: credential.authenticatorAttachment || '',
          response: {
            clientDataJSON: toBase64(credential.response.clientDataJSON),
            authenticatorData: toBase64((credential.response as any).authenticatorData),
            signature: toBase64((credential.response as any).signature),
          },
        },
        authMethod: 'COOKIE',
        mfaScope,
        mfaSessionToken,
        authToken,
        decodeChallenge: true,
      });

      onComplete?.();
      store.set(verifyStepAtom, 'VERIFIED');
    } catch (error) {
      snackbar.error({ message: getErrorMessage(error) });
      onFail?.();
      store.set(verifyStepAtom, 'FAIL');
    }
  }
  return {
    verifyStepAtom,
    mfaWithPasskey,
  };
});

export type PasskeyListPhase =
  | PasskeyListPhaseNone
  | PasskeyListPhaseEnablePasskeyOrPasskeyList
  | PasskeyListPhaseAreYouSureWantToRemovePasskey
  | PassKeyListPhaseVerifyingWhenRemovingPasskey;
interface PasskeyListPhaseBase<T extends string> {
  type: T;
}
interface PasskeyListPhaseNone extends PasskeyListPhaseBase<'none'> {}
interface PasskeyListPhaseEnablePasskeyOrPasskeyList extends PasskeyListPhaseBase<'enable-passkey-or-passkey-list'> {}
interface PasskeyListPhaseAreYouSureWantToRemovePasskey
  extends PasskeyListPhaseBase<'are-you-sure-want-to-remove-passkey'> {
  id: string;
}
interface PassKeyListPhaseVerifyingWhenRemovingPasskey extends PasskeyListPhaseBase<'verifying-when-removing-passkey'> {
  id: string;
}

export const passkeyListFlowBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const passkeyApiBunjaInstance = bunja.use(passkeyApiBunja);
  const { getUserPasskeyCredentialsAtom, credentialExistsAtom } = passkeyApiBunjaInstance;
  const currentPhaseAtom = atom<PasskeyListPhase>({ type: 'none' });
  function openPasskeyListModal() {
    store.set(currentPhaseAtom, { type: 'enable-passkey-or-passkey-list' });
  }
  function closePasskeyListModal() {
    store.set(currentPhaseAtom, { type: 'none' });
  }
  const passkeyListItemsAtom = atom((get) => {
    const { data: userPasskeyCredentials } = get(getUserPasskeyCredentialsAtom);
    const credentials = userPasskeyCredentials?.data.credentials || [];
    return credentials.map((credential) => {
      const added = convertDate(credential.createdAt) || new Date(NaN);
      const lastUsed = convertDate(credential.lastLoggedInAt) || added;
      return {
        id: credential.credentialId,
        title: `PassKey on ${credential.device}`,
        added,
        lastUsed,
      } satisfies PasskeyItem;
    });
  });
  const isEnablePasskeyModalShowAtom = atom((get) => {
    const currentPhase = get(currentPhaseAtom);
    const credentialExists = get(credentialExistsAtom);
    return !credentialExists && currentPhase.type === 'enable-passkey-or-passkey-list';
  });
  const isPasskeyListModalShowAtom = atom((get) => {
    const currentPhase = get(currentPhaseAtom);
    const credentialExists = get(credentialExistsAtom);
    return credentialExists && currentPhase.type === 'enable-passkey-or-passkey-list';
  });
  return {
    currentPhaseAtom,
    isEnablePasskeyModalShowAtom,
    isPasskeyListModalShowAtom,
    openPasskeyListModal,
    closePasskeyListModal,
    passkeyListItemsAtom,
  };
});

export const passkeyAddFlowBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const passkeyApiBunjaInstance = bunja.use(passkeyApiBunja);
  const { getUserPasskeyCredentialsAtom } = passkeyApiBunjaInstance;
  const addingPasskeyAtom = atom(false);

  async function addPasskey(mfaSessionToken: string) {
    try {
      store.set(addingPasskeyAtom, true);
      await registerPasskey(mfaSessionToken);
      await store.get(getUserPasskeyCredentialsAtom).refetch();
      snackbar.success({ message: 'Your passkey was successfully added' });
    } catch (error) {
      snackbar.error({ message: getErrorMessage(error) });
    } finally {
      store.set(addingPasskeyAtom, false);
    }
  }
  return { addingPasskeyAtom, addPasskey };
});

export const passkeyRemoveFlowBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const passkeyApiBunjaInstance = bunja.use(passkeyApiBunja);
  const passkeyListFlowBunjaInstance = bunja.use(passkeyListFlowBunja);
  const { getUserPasskeyCredentialsAtom } = passkeyApiBunjaInstance;
  const { currentPhaseAtom } = passkeyListFlowBunjaInstance;
  const verifyingPasskeyAtom = atom(false);
  function openAreYouSureModal(id: string) {
    store.set(currentPhaseAtom, { type: 'are-you-sure-want-to-remove-passkey', id });
  }
  function closeAreYouSureModal() {
    store.set(currentPhaseAtom, { type: 'enable-passkey-or-passkey-list' });
  }

  async function removePasskey(mfaSessionToken: string) {
    const { id } = store.get(currentPhaseAtom) as PasskeyListPhaseAreYouSureWantToRemovePasskey;
    store.set(currentPhaseAtom, { type: 'verifying-when-removing-passkey', id });
    try {
      store.set(verifyingPasskeyAtom, true);
      await deleteUserPasskeyCredentialApi(id, { mfaSessionToken });
      const { data } = await store.get(getUserPasskeyCredentialsAtom).refetch();
      const isCredentialsRemain = Number(data?.data.credentials.length) > 0;
      snackbar.success({ message: 'Your passkey has been removed' });
      store.set(currentPhaseAtom, { type: isCredentialsRemain ? 'enable-passkey-or-passkey-list' : 'none' });
    } catch (error) {
      snackbar.error({ message: getErrorMessage(error) });
    } finally {
      store.set(verifyingPasskeyAtom, false);
    }
  }
  const isAreYouSureModalShowAtom = atom((get) => {
    const currentPhase = get(currentPhaseAtom);
    return currentPhase.type === 'are-you-sure-want-to-remove-passkey';
  });
  return {
    isAreYouSureModalShowAtom,
    openAreYouSureModal,
    closeAreYouSureModal,
    removePasskey,
  };
});
